import { PhoneOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import cn from 'classnames';
import { FieldInputProps, FieldMetaProps } from 'formik';
import React, { HTMLProps, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '~/components/Typo/Typo';
import CustomTextField from '~/components/fields/CustomTextField/CustomTextField';
import Loader from '~/components/Loader/Loader';
import { GimacCountry, GimacParticipant, isGimacMobileMoneyParticipant } from '~/entities/schemas/gimacCountry';
import { formatNumberAuthorizeByCountry } from '~/utils/handle-input-events';
import { FontWeight } from '~/theme/constants';

import { detectOperatorFromPhone, getDialCodeForCountry, getFlagForCountry } from './operatorPrefixRules';
import { getParticipantLogo } from './operatorLogoMapping';

import './GimacForm.scss';

export interface GimacFormProps extends HTMLProps<HTMLFormElement> {
  loading?: boolean;
  loadingCountries?: boolean;
  gimacCountries: GimacCountry[];
  getFieldProps: (name: string) => FieldInputProps<any>;
  getFieldMeta: (name: string) => FieldMetaProps<any>;
  handleChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: any;
  setFieldTouched?: any;
  values?: any;
  onCountryChange?: (dialCode: string) => void;
  onOperatorDetected?: (operator: GimacParticipant | null) => void;
  waitingValidation?: boolean;
  headerIcons?: React.ReactNode[];
}

const GimacForm: React.FC<GimacFormProps> = ({
  loading,
  loadingCountries,
  gimacCountries,
  getFieldProps,
  getFieldMeta,
  handleChange,
  setFieldValue,
  setFieldTouched,
  values,
  onCountryChange,
  onOperatorDetected,
  waitingValidation,
  headerIcons,
}) => {
  const { t } = useTranslation();

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [countrySelected, setCountrySelected] = useState<boolean>(false);
  const [detectedOperator, setDetectedOperator] = useState<GimacParticipant | null>(null);

  // Participants MM actifs pour le pays selectionne
  const mmParticipants = useMemo(() => {
    if (!selectedCountryCode) return [];
    const country = gimacCountries.find(c => c.code === selectedCountryCode);
    if (!country) return [];
    return country.participants.filter(isGimacMobileMoneyParticipant);
  }, [selectedCountryCode, gimacCountries]);

  // Handler pour le changement de pays
  // On ne passe PAS par handleChange(e) car le select utilise countryCode comme value
  // alors que Formik attend le dialCode pour le champ "indicatif"
  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const countryCode = e.target.value;

      if (countryCode === '') {
        setCountrySelected(false);
        setSelectedCountryCode('');
        setDetectedOperator(null);
        setFieldValue('indicatif', '');
        setFieldValue('phoneNumber', '');
        if (setFieldTouched) setFieldTouched('indicatif', true, false);
        localStorage.removeItem('countrySelectedOne');
      } else {
        const dialCode = getDialCodeForCountry(countryCode);
        setSelectedCountryCode(countryCode);
        setCountrySelected(true);
        setDetectedOperator(null);
        setFieldValue('indicatif', dialCode);
        setFieldValue('phoneNumber', '');
        if (setFieldTouched) setFieldTouched('indicatif', true, false);
        localStorage.setItem('countrySelectedOne', dialCode);

        if (onCountryChange) {
          onCountryChange(dialCode);
        }
      }
    },
    [setFieldValue, onCountryChange],
  );

  // Detection automatique de l'operateur quand le numero change
  const currentPhone = values?.phoneNumber || '';
  useEffect(() => {
    if (countrySelected && currentPhone.length >= 3 && mmParticipants.length > 0) {
      const operator = detectOperatorFromPhone(currentPhone, mmParticipants);
      setDetectedOperator(operator);
      if (onOperatorDetected) {
        onOperatorDetected(operator);
      }
    } else {
      setDetectedOperator(null);
      if (onOperatorDetected) {
        onOperatorDetected(null);
      }
    }
  }, [countrySelected, currentPhone, mmParticipants, onOperatorDetected]);

  const isOperatorDetected = (participant: GimacParticipant): boolean =>
    detectedOperator !== null && detectedOperator.code === participant.code;

  if (waitingValidation) {
    return (
      <div className="gimac-form gimac-form--waiting">
        <div className="waiting-validation">
          <Loader />
          <Text className="waiting-validation-message" weight={FontWeight.MEDIUM}>
            {t('gimacForm:transactionWaitingValidation')}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="gimac-form">
      <div className="gimac-header">
        <div className="gimac-header-caption">
          <Text className="caption" weight={FontWeight.BOLD}>
            {t('gimacForm:operatorsAvailable')}
          </Text>
        </div>
        {headerIcons && headerIcons.length > 0 && (
          <div className="gimac-header-icons">
            {headerIcons.map((icon, i) => (
              <span key={i}>{icon}</span>
            ))}
          </div>
        )}
      </div>

      {/* Selecteur de pays */}
      <div className="country-selector-wrapper">
        <Row gutter={[30, 30]} style={{ backgroundColor: 'transparent', width: '100%', margin: '0 auto' }}>
          <Col span={24} md={24} className="pad">
            {loadingCountries ? (
              <Row justify="center">
                <Loader />
              </Row>
            ) : (
              <>
                <select
                  title={t('gimacForm:selectCountry')}
                  className={cn(
                    'option-field suffix-icon-wrapper',
                    getFieldMeta('indicatif').error ? 'select2 option-field suffix-icon-wrapper' : '',
                  )}
                  name="indicatif"
                  onChange={handleCountryChange}
                  value={selectedCountryCode}
                  disabled={loading}
                >
                  <option value="">{t('gimacForm:selectCountry')}</option>
                  {gimacCountries.map(country => {
                    const dialCode = getDialCodeForCountry(country.code);
                    const dialLabel = dialCode ? `(+${dialCode}) ` : '';
                    return (
                      <option key={country.code} value={country.code}>
                        ({getFlagForCountry(country.code)}) {dialLabel}
                        {country.name}
                      </option>
                    );
                  })}
                </select>
                <div className="text-danger">
                  {getFieldMeta('indicatif').touched && !values?.indicatif && getFieldMeta('indicatif').error}
                </div>
              </>
            )}
          </Col>
        </Row>
      </div>

      {/* Affichage des operateurs MM disponibles */}
      {countrySelected && mmParticipants.length > 0 && (
        <div className="operators-wrapper">
          <Row justify="center">
            {mmParticipants.map(participant => {
              const logo = getParticipantLogo(participant);
              return (
                <Col
                  key={participant.code}
                  lg={4}
                  md={6}
                  sm={8}
                  xs={12}
                  style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}
                >
                  <div
                    className={cn('operator-card', { detected: isOperatorDetected(participant) })}
                    title={participant.name}
                  >
                    {logo ? (
                      <img src={logo} alt={participant.shortName} className="operator-logo" />
                    ) : (
                      <Text className="operator-name" weight={FontWeight.MEDIUM}>
                        {participant.shortName}
                      </Text>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {countrySelected && mmParticipants.length === 0 && (
        <div className="no-operators">
          <Text className="no-operators-text">{t('gimacForm:noOperatorsAvailable')}</Text>
        </div>
      )}

      {/* Champ de numero de telephone */}
      {countrySelected ? (
        <div className="phone-field-wrapper">
          <Row gutter={[30, 30]} style={{ backgroundColor: 'transparent', width: '60%', margin: '0 auto' }}>
            <Col span={24} md={24} className="pad">
              <CustomTextField
                name="phoneNumber"
                type="text"
                label=""
                placeholder={t('gimacForm:placeholderPhone')}
                meta={getFieldMeta('phoneNumber')}
                field={getFieldProps('phoneNumber')}
                onChange={handleChange}
                onInput={formatNumberAuthorizeByCountry}
                inputMode="numeric"
                suffixIcon={<PhoneOutlined className="suffix-icon" />}
                required
                disabled={loading}
              />
              <div className="text-danger">
                {getFieldMeta('phoneNumber').error && getFieldMeta('phoneNumber').error}
              </div>
            </Col>
          </Row>

          {/* Indicateur d'operateur detecte */}
          {detectedOperator && (
            <div className="operator-detected-info">
              <Text className="detected-text" weight={FontWeight.MEDIUM}>
                {t('gimacForm:operatorDetected')}: {detectedOperator.name}
              </Text>
            </div>
          )}
        </div>
      ) : (
        <div className="no-country-selected">
          <Text className="select-country-text">{t('gimacForm:selectCountry')}</Text>
        </div>
      )}
    </div>
  );
};

export default GimacForm;
